import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import TerminateLoan from '@app/components/popups/TerminateLoan';
import { DATE_FORMAT } from '@app/configs/dateTime';
import { LoanPurposes, LoanStatuses } from '@app/configs/loanApplication';
import { ROUTES } from '@app/configs/routes';
import useBoolean from '@app/hooks/useBoolean';
import { getLocalDateTime } from '@app/utils/time';
import formatAmount from '@modules/calculator/utils/formatAmount';
import LoanStatus from './LoanStatus';
const ActiveLoan = ({ loanApplication }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoanTerminateOpen, { setTrue: openTerminate, setFalse: closeTerminate }] = useBoolean(false);
    const loanPurpose = loanApplication.purpose === LoanPurposes.Empty
        ? loanApplication.id
        : t(`financing.loan_purpose.${loanApplication.purpose}`);
    const loanApplicationCollectedPercent = Math.round((loanApplication.collectedAmount / loanApplication.amount) * 10000) / 100;
    const nextPaymentDate = loanApplication.nextPaymentDate
        ? getLocalDateTime(loanApplication.nextPaymentDate, DATE_FORMAT.dateDash)
        : '';
    const investmentDeadline = loanApplication.fundingDate
        ? getLocalDateTime(loanApplication.fundingDate, DATE_FORMAT.dateTime)
        : '';
    const { nextPaymentAmount } = loanApplication;
    const isFunding = [LoanStatuses.Collected, LoanStatuses.Collecting].includes(loanApplication.status);
    const isFundingOrSubmitted = [LoanStatuses.Submitted, LoanStatuses.Collected, LoanStatuses.Collecting].includes(loanApplication.status);
    const isActiveLoan = [LoanStatuses.Overdue, LoanStatuses.Active].includes(loanApplication.status);
    const handleGoToLoanDetails = () => {
        navigate(ROUTES.loanDetails.replace(':id', `${loanApplication.id}`));
    };
    return (<>
            <TerminateLoan isOpen={isLoanTerminateOpen} closeModal={closeTerminate} loanId={loanApplication.id}/>

            <Card>
                <h2 className="title-l mb-6">{`${t('financing.active_loan.my_loan')}: ${loanPurpose}`}</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4 pb-6 mb-6 border-b border-neutral-primary-initial">
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {isActiveLoan ? t('financing.active_loan.id') : t('financing.active_application.id')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">{loanApplication.id}</div>
                    </div>
                    <div>
                        <div className="body-s text-neutral-secondary-initial">{t('financing.active_loan.status')}</div>
                        <div className="mt-0.5">
                            <LoanStatus status={loanApplication.status}/>
                        </div>
                    </div>
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {isActiveLoan ? t('financing.active_loan.amount') : t('financing.active_requested.amount')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">
                            {formatAmount(loanApplication.amount)}
                        </div>
                    </div>
                    {isFundingOrSubmitted && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.active_loan.collected_amount')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {formatAmount(loanApplication.collectedAmount)} ({loanApplicationCollectedPercent}%)
                            </div>
                        </div>)}
                    {nextPaymentDate && isActiveLoan && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.active_loan.next_payment_date')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">{nextPaymentDate}</div>
                        </div>)}
                    {nextPaymentAmount && isActiveLoan && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.active_loan.next_payment_amount')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {formatAmount(nextPaymentAmount)}
                            </div>
                        </div>)}
                    <div>
                        <div className="body-s text-neutral-secondary-initial">{t('financing.active_loan.period')}</div>
                        <div className="label-m text-neutral-primary-initial">
                            {`${loanApplication.period} ${t('financing.investing_page_table.months')}`}
                        </div>
                    </div>
                    {investmentDeadline && isFunding && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.active_loan.investment_deadline')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">{investmentDeadline}</div>
                        </div>)}
                </div>

                <div className="flex gap-4">
                    <Button variant="primary" size="md" label={t('financing.active_loan.view_details')} iconLeft={<Icon name="visibility" size="sm"/>} onClick={handleGoToLoanDetails}/>
                    {loanApplication.canBeTerminated && (<Button variant="critical" size="md" label={t('financing.active_loan.terminate')} onClick={openTerminate}/>)}
                </div>
            </Card>
        </>);
};
export default ActiveLoan;
